import { CHARGE_STATUS } from '@/types/charge-status';
import { ENROLLMENT_STATUS } from '@/types/enrollment-status';
import { cn } from '@/utils/cn';
import React from 'react';
import { Text, View } from 'react-native';

export type StatusBadgeType =
  | { type: 'charge'; value: CHARGE_STATUS }
  | { type: 'enrollment'; value: ENROLLMENT_STATUS }
  | { type: 'service'; value: boolean } // isActive
  | {
      type: 'custom';
      value: string;
      label?: string;
      variant?: 'success' | 'warning' | 'error' | 'info' | 'neutral';
    };

interface StatusBadgeProps {
  status: StatusBadgeType;
  className?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
}

/** Tom visual do badge: pill suave com ponto colorido */
type Tone = 'success' | 'warning' | 'error' | 'info' | 'neutral' | 'primary' | 'premium';

const toneClasses: Record<Tone, { container: string; text: string; dot: string }> = {
  success: { container: 'bg-success-50', text: 'text-success-600', dot: 'bg-success-500' },
  warning: { container: 'bg-warning-50', text: 'text-warning-600', dot: 'bg-warning-500' },
  error: { container: 'bg-error-50', text: 'text-error-600', dot: 'bg-error-500' },
  info: { container: 'bg-info-50', text: 'text-info-600', dot: 'bg-info-500' },
  neutral: { container: 'bg-neutral-100', text: 'text-neutral-600', dot: 'bg-neutral-400' },
  primary: { container: 'bg-primary-50', text: 'text-primary-700', dot: 'bg-primary-500' },
  premium: { container: 'bg-premium-50', text: 'text-premium-600', dot: 'bg-premium-500' },
};

const chargeLabels: Record<CHARGE_STATUS, string> = {
  [CHARGE_STATUS.PAID]: 'Pago',
  [CHARGE_STATUS.PENDING]: 'Pendente',
  [CHARGE_STATUS.OVERDUE]: 'Vencido',
  [CHARGE_STATUS.CANCELED]: 'Cancelado',
  [CHARGE_STATUS.REFUNDED]: 'Reembolsado',
  [CHARGE_STATUS.PARTIALLY_REFUNDED]: 'Reembolso parcial',
  [CHARGE_STATUS.IN_DISPUTE]: 'Em disputa',
  [CHARGE_STATUS.PAYMENT_REPORTED]: 'Pagamento informado',
};

const chargeTones: Record<CHARGE_STATUS, Tone> = {
  [CHARGE_STATUS.PAID]: 'success',
  [CHARGE_STATUS.PENDING]: 'warning',
  [CHARGE_STATUS.OVERDUE]: 'error',
  [CHARGE_STATUS.CANCELED]: 'neutral',
  [CHARGE_STATUS.REFUNDED]: 'neutral',
  [CHARGE_STATUS.PARTIALLY_REFUNDED]: 'neutral',
  [CHARGE_STATUS.IN_DISPUTE]: 'warning',
  [CHARGE_STATUS.PAYMENT_REPORTED]: 'premium',
};

const enrollmentLabels: Record<ENROLLMENT_STATUS, string> = {
  [ENROLLMENT_STATUS.ACTIVE]: 'Ativo',
  [ENROLLMENT_STATUS.INACTIVE]: 'Inativo',
  [ENROLLMENT_STATUS.PAUSED]: 'Pausado',
  [ENROLLMENT_STATUS.CANCELLED]: 'Cancelado',
  [ENROLLMENT_STATUS.COMPLETED]: 'Concluído',
  [ENROLLMENT_STATUS.PENDING_APPROVAL]: 'Aguardando',
};

const enrollmentTones: Record<ENROLLMENT_STATUS, Tone> = {
  [ENROLLMENT_STATUS.ACTIVE]: 'primary',
  [ENROLLMENT_STATUS.INACTIVE]: 'neutral',
  [ENROLLMENT_STATUS.PAUSED]: 'warning',
  [ENROLLMENT_STATUS.CANCELLED]: 'error',
  [ENROLLMENT_STATUS.COMPLETED]: 'success',
  [ENROLLMENT_STATUS.PENDING_APPROVAL]: 'warning',
};

const sizeContainerClasses = {
  xs: 'px-1.5 py-0.5 gap-1',
  sm: 'px-2 py-0.5 gap-1',
  md: 'px-2.5 py-1 gap-1.5',
  lg: 'px-3 py-1 gap-1.5',
} as const;

const sizeTextClasses = {
  xs: 'text-[9px]',
  sm: 'text-[10px]',
  md: 'text-[11px]',
  lg: 'text-xs',
} as const;

const sizeDotClasses = {
  xs: 'h-1 w-1',
  sm: 'h-1 w-1',
  md: 'h-1.5 w-1.5',
  lg: 'h-1.5 w-1.5',
} as const;

export function StatusBadge({ status, className, size = 'sm' }: StatusBadgeProps) {
  let label: string;
  let tone: Tone;

  switch (status.type) {
    case 'charge':
      label = chargeLabels[status.value];
      tone = chargeTones[status.value];
      break;
    case 'enrollment':
      label = enrollmentLabels[status.value];
      tone = enrollmentTones[status.value];
      break;
    case 'service':
      label = status.value ? 'Ativo' : 'Inativo';
      tone = status.value ? 'primary' : 'neutral';
      break;
    case 'custom':
      label = status.label || status.value;
      tone = status.variant === 'info' ? 'primary' : (status.variant ?? 'neutral');
      break;
  }

  const classes = toneClasses[tone];

  return (
    <View
      className={cn(
        'flex-row items-center self-start rounded-full',
        sizeContainerClasses[size],
        classes.container,
        className,
      )}
    >
      <View className={cn('rounded-full', sizeDotClasses[size], classes.dot)} />
      <Text className={cn('font-semibold', sizeTextClasses[size], classes.text)}>{label}</Text>
    </View>
  );
}
